import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  StaffResponse,
  TreatmentPlanDto,
  TreatmentPlanTemplateResponse,
  YearPlanResponse
} from '../../api/generated';
import SparklesIcon from '../../assets/icons/sparkles.svg?react';
import { useStaffCompetences } from '../../hooks/useStaffCompetences';
import { useYearPlanSimulation } from '../../hooks/useYearPlanSimulation';
import { useYearPlanSimulationForm } from '../../hooks/useYearPlanSimulationForm';
import { summarizeSimulationProgress } from '../../services/simulationProgressService';
import { Modal } from '../UI';
import { Button } from '../UI/base/Button';
import { SimulationProgress } from './SimulationProgress';
import { SimulationSettingsForm } from './SimulationSettingsForm';
import styles from './YearPlanSimulationModal.module.css';

const MODAL_WIDTH = 720;

interface YearPlanSimulationModalProps {
  yearPlan: YearPlanResponse;
  treatmentPlanTemplates: TreatmentPlanTemplateResponse[];
  // Undefined until the location's roster has loaded; the simulation cannot
  // judge capacity before then.
  staff?: readonly StaffResponse[];
  // The location's patient rooms, one bed each; undefined until they have
  // loaded, on the same terms as `staff`.
  bedCount?: number;
  // Staff or patient rooms failed to load, so they will not arrive without a
  // reload; the form says so instead of waiting for them.
  hasResourcesLoadError: boolean;
  onClose: () => void;
  onApply: (plans: TreatmentPlanDto[]) => void;
}

export const YearPlanSimulationModal = ({
  yearPlan,
  treatmentPlanTemplates,
  staff,
  bedCount,
  hasResourcesLoadError,
  onClose,
  onApply
}: YearPlanSimulationModalProps) => {
  const { t } = useTranslation();
  const form = useYearPlanSimulationForm(yearPlan, treatmentPlanTemplates);
  const { status, input, log, result, run, reset } = useYearPlanSimulation();
  const { competences } = useStaffCompetences();

  const professionNames = useMemo(
    () => new Map(competences.map((c) => [c.id, c.name])),
    [competences]
  );
  const professionName = useCallback(
    (professionId: string): string =>
      professionNames.get(professionId) ?? t('simulation-unknown-profession'),
    [professionNames, t]
  );

  const progress = useMemo(
    () => (input ? summarizeSimulationProgress(input, log) : null),
    [input, log]
  );

  const isResourcesLoaded = staff !== undefined && bedCount !== undefined;

  const handleRun = (): void => {
    if (staff && bedCount !== undefined && form.canRun) run(form.buildInput(staff, bedCount));
  };

  const handleApply = (): void => {
    if (result && result.plans.length > 0) onApply(result.plans);
  };

  const renderFooter = () => {
    if (status === 'idle') {
      return (
        <>
          <Button type="secondary" onClick={onClose}>
            {t('simulation-cancel')}
          </Button>
          <Button
            type="primary"
            icon={<SparklesIcon className={styles.buttonIcon} />}
            disabled={!isResourcesLoaded || !form.canRun}
            onClick={handleRun}
          >
            {t('simulation-run')}
          </Button>
        </>
      );
    }
    if (status === 'running') {
      return (
        <Button type="secondary" onClick={reset}>
          {t('simulation-stop')}
        </Button>
      );
    }
    return (
      <>
        <Button type="secondary" onClick={reset}>
          {t('simulation-back')}
        </Button>
        <Button
          type="primary"
          disabled={!result || result.plans.length === 0}
          onClick={handleApply}
        >
          {t('simulation-apply', { count: result?.plans.length ?? 0 })}
        </Button>
      </>
    );
  };

  return (
    <Modal
      title={t('simulation-title')}
      onClose={onClose}
      width={MODAL_WIDTH}
      footer={<div className={styles.footer}>{renderFooter()}</div>}
    >
      {status === 'idle' || !progress ? (
        <SimulationSettingsForm
          form={form}
          weeks={yearPlan.weeks}
          isResourcesLoaded={isResourcesLoaded}
          hasResourcesLoadError={hasResourcesLoadError}
        />
      ) : (
        <SimulationProgress
          status={status}
          progress={progress}
          result={result}
          professionName={professionName}
        />
      )}
    </Modal>
  );
};
